import { escapePath } from "dot-prop";
import {
  Component,
  createDeferred,
  createMemo,
  createSignal,
  JSX,
  Setter,
  Signal,
  createContext,
  splitProps,
  createEffect,
  onCleanup,
  useContext,
  batch,
} from "solid-js";
import { MemoOptions } from "solid-js/types/reactive/signal";
import {
  SafeParseReturnType,
  z,
  ZodError,
  ZodIssue,
  ZodIssueCode,
  ZodTypeAny,
} from "zod";
import { Get } from "./get";
import { PartialDeep } from "./partial-deep";
import { acquireLock, createAsyncMemo, deepSignal } from "./signals";

export type PartialZodInput<S extends ZodTypeAny> = PartialDeep<z.input<S>>;
export type GetZodInput<S extends ZodTypeAny, P extends string> = Get<
  z.input<S>,
  P
>;

export type ValidationAccessor<T> = (() => T | undefined) & {
  readonly path: string;
  readonly dirty: boolean;
  readonly issues: ZodIssue[];
};

export type ValidationSignal<T> = [
  ValidationAccessor<T>,
  Setter<T | undefined>
];

export type SubmitHandler<T> = (values: T) => void | Promise<void>;
type ZodForm<S extends ZodTypeAny> = Component<
  {
    handleSubmit: SubmitHandler<z.output<S>>;
  } & JSX.FormHTMLAttributes<HTMLFormElement>
>;

type ValidationState<S extends ZodTypeAny> = {
  valid(): boolean;
  validating(): boolean;
  input(): PartialZodInput<S>;
  output(): z.output<S> | undefined;
  reset(input?: PartialZodInput<S>): void;
  bind<P extends string>(
    path: P,
    options?: MemoOptions<GetZodInput<S, P> | undefined> & { debounce?: number }
  ): ValidationSignal<GetZodInput<S, P>>;
  submitting(): boolean;
  submit(handler: SubmitHandler<z.output<S>>): Promise<void>;
  lastSubmit(): SafeParseReturnType<z.input<S>, z.output<S>> | undefined;
};

const formContext = createContext<ValidationState<any>>();
export const useValidationState = <S extends ZodTypeAny>() => {
  const ctx = useContext<ValidationState<S> | undefined>(formContext);
  if (ctx === undefined) {
    throw new Error("outside form context");
  }
  return ctx;
};

export const createZodForm = <S extends ZodTypeAny>(options: {
  schema: S;
  initialInput: PartialZodInput<S>;
  timeoutMs?: number;
}): [ZodForm<S>, ValidationState<S>] => {
  const { schema, initialInput, timeoutMs } = options;
  const [input, setInput] = createSignal<PartialDeep<z.input<S>>>(
    initialInput,
    {
      equals: false,
    }
  );
  const validation = createAsyncMemo(
    createDeferred(input, {
      timeoutMs,
    }),
    (input) => schema.safeParseAsync(input)
  );
  const issues = createMemo(() => {
    const result = validation.latest();
    return result?.success === false ? collectZodIssues(result.error) : {};
  });
  const [submitting, setSubmitting] = createSignal(false);
  const [lastSubmit, setLastSubmit] = createSignal<
    SafeParseReturnType<z.input<S>, z.output<S>> | undefined
  >();

  const [dirty, setDirty] = createSignal<Record<string, boolean>>(
    {},
    { equals: false }
  );

  const touch = (name: string) => setDirty((d) => ({ ...d, [name]: true }));
  const valid = createMemo(() => validation.result()?.success ?? false);
  const validating = createMemo(() => validation.pending());
  const output = createMemo(() => {
    const result = validation.result();
    return result?.success === true ? result.data : undefined;
  });
  const state: ValidationState<S> = {
    valid,
    submitting,
    lastSubmit,
    validating,
    input,
    output,
    reset(input?: PartialZodInput<S>): void {
      batch(() => {
        setInput(input ?? initialInput);
        setDirty({});
      });
    },
    bind<P extends string>(
      path: P,
      options?: {
        equals?(
          oldValue: GetZodInput<S, P> | undefined,
          newValue: GetZodInput<S, P> | undefined
        ): boolean;
      }
    ) {
      const [value, setValue] = deepSignal(
        [input, setInput] as Signal<z.input<S>>,
        path,
        options
      );
      const props = {
        path: () => path,
        dirty: createMemo(() => dirty()[path] ?? false),
        issues: createMemo(() => issues()[path] ?? []),
      };
      const accessor = new Proxy(value, {
        get(_target: any, key: keyof typeof props) {
          return props[key]();
        },
      });
      const setter = (value?: any) => {
        touch(path);
        return setValue(value);
      };
      return [accessor, setter];
    },
    async submit(handler) {
      const lock = acquireLock(setSubmitting);
      if (lock === undefined) {
        return;
      }
      try {
        const result = await schema.safeParseAsync(input());
        setLastSubmit(result);
        if (result.success) {
          await handler(result.data);
        } else {
          const issues = collectZodIssues(result.error);
          setDirty((prev) =>
            Object.keys(issues).reduce(
              (dirty, key) => ({
                ...dirty,
                [key]: true,
              }),
              prev
            )
          );
        }
      } finally {
        lock.release();
      }
    },
  };
  const Form: ZodForm<S> = (props) => {
    const [local, formProps] = splitProps(props, ["handleSubmit"]);
    let ref: HTMLFormElement | undefined;
    createEffect(() => {
      const onSubmit = async (e: SubmitEvent) => {
        e.preventDefault();
        e.stopPropagation();
        state.submit(local.handleSubmit);
      };
      ref?.addEventListener("submit", onSubmit);
      onCleanup(() => ref?.removeEventListener("submit", onSubmit));
    });
    return (
      <formContext.Provider value={state}>
        <form ref={ref} noValidate {...formProps} />
      </formContext.Provider>
    );
  };
  return [Form, state];
};

function joinIssuePath(path: (string | number)[]) {
  return path.length === 0
    ? ""
    : path
        .map((index) =>
          typeof index === "string" ? `.${escapePath(index)}` : `[${index}]`
        )
        .join("")
        .replace(/^\./, "");
}

function* zodIssues<T>(
  error: ZodError<T>,
  path: (string | number)[] = []
): Generator<ZodIssue> {
  for (let issue of error.issues) {
    const fullPath = [...path, ...issue.path];
    yield { ...issue, path: fullPath };
    if (issue.code === ZodIssueCode.invalid_union) {
      for (let unionError of issue.unionErrors) {
        yield* zodIssues(unionError, [...path]);
      }
    }
  }
  return;
}

function collectZodIssues<S extends ZodTypeAny>(error: ZodError<z.input<S>>) {
  const issuesByPath: Record<string, ZodIssue[]> = {};
  for (let issue of zodIssues(error)) {
    const path = joinIssuePath(issue.path);
    issuesByPath[path] = [...(issuesByPath[path] ?? []), issue];
  }
  return issuesByPath;
}
